import _ from 'lodash';
import { Link, useHistory } from 'react-router-dom';
import { useEffect, Fragment, useState, useRef, useContext } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import PropTypes from 'prop-types';
import {
  useQuery,
  useMutation,
  useSubscription,
  ApolloError,
  gql,
} from '@apollo/client';
import { ClientWithOnReconnected } from '../../utils/apollo';
import { GlobalNotificationContext } from '../context/GlobalNotification';
import { IFilestackFileUpload } from '../../utils/filestack';
import logError from '../../utils/airbrake';
import tokenUtils from '../../utils/token';
import errorUtils from '../../utils/error';
import EmailMessageThreadEvent from '../threadEvent/EmailMessageThreadEvent';
import TextMessageThreadEvent from '../threadEvent/TextMessageThreadEvent';
import MessageThreadEvent from '../threadEvent/MessageThreadEvent';
import PhoneCallThreadEvent from '../threadEvent/PhoneCallThreadEvent';
import MeetingThreadEvent from '../threadEvent/MeetingThreadEvent';
import EmailEditor from '../feature/EmailEditor';
import TextEditor from '../feature/TextEditor';
import PhoneCallEditor from '../feature/PhoneCallEditor';
import OrphanClientEditor from '../feature/OrphanClientEditor';
import {
  OrphanDetail,
  TextMessageEventDetail,
  EmailMessageEventDetail,
  MessageEventDetail,
  MeetingEventDetail,
  QuoteDetail,
  PhoneCallEventDetail,
  RequestEventDetail,
  MatchDetail,
  FileUploadDetail,
} from '../../utils/gql';
import { replyToSubject } from '../../utils/format';
import {
  TemplatesQuery,
  OrphanDetailsQuery,
  OrphanDetailsQueryVariables,
  OrphanThreadPaginatedQuery,
  OrphanThreadPaginatedQueryVariables,
  OrphanThreadIntoBrandMutation,
  OrphanThreadIntoBrandMutationVariables,
  OrphanThreadEventUpdatedSubscription,
  OrphanThreadEventUpdatedSubscriptionVariables,
  OrphanFileUploadUpdatedSubscription,
  OrphanFileUploadUpdatedSubscriptionVariables,
  OrphanThreadSendTextMessageMutation,
  OrphanThreadSendTextMessageMutationVariables,
  MeetingInviteResponseOrphanMutation,
  MeetingInviteResponseOrphanMutationVariables,
  OrphanThreadSendEmailMessageMutation,
  OrphanThreadSendEmailMessageMutationVariables,
  OrphanThreadOutboundPhoneCallMutation,
  OrphanThreadOutboundPhoneCallMutationVariables,
  ExpertDetailsQuery,
  OrphanArchiveUnarchiveMutation,
  OrphanArchiveUnarchiveMutationVariables,
} from '../../gql/graphql';

const orphanDetailsQuery = gql`
  query OrphanDetails($orphanId: ID!) {
    orphanDetails(orphanId: $orphanId) {
      ...OrphanDetail
    }
  }
  ${OrphanDetail}
`;

// @connection(key: "thread", filter: ["orphanId"])
const orphanThreadPaginatedQuery = gql`
  query OrphanThreadPaginated(
    $orphanId: ID
    $direction: String!
    $fromDate: Date
    $limit: Int!
  ) {
    threadPaginated(
      orphanId: $orphanId
      direction: $direction
      fromDate: $fromDate
      limit: $limit
    ) {
      id
      edges {
        id
        cursor
        node {
          ... on TextMessageEvent {
            ...TextMessageEventDetail
          }
          ... on EmailMessageEvent {
            ...EmailMessageEventDetail
          }
          ... on RequestEvent {
            ...RequestEventDetail
          }
          ... on MessageEvent {
            ...MessageEventDetail
          }
          ... on MeetingEvent {
            ...MeetingEventDetail
          }
          ... on Quote {
            ...QuoteDetail
          }
          ... on PhoneCallEvent {
            ...PhoneCallEventDetail
          }
        }
      }
    }
  }
  ${TextMessageEventDetail}
  ${EmailMessageEventDetail}
  ${RequestEventDetail}
  ${MessageEventDetail}
  ${MeetingEventDetail}
  ${QuoteDetail}
  ${PhoneCallEventDetail}
`;

const orphanThreadEventUpdatedSubscription = gql`
  subscription OrphanThreadEventUpdated($threadId: ID!) {
    threadEventUpdated(threadId: $threadId) {
      ... on TextMessageEvent {
        ...TextMessageEventDetail
      }
      ... on EmailMessageEvent {
        ...EmailMessageEventDetail
      }
      ... on RequestEvent {
        ...RequestEventDetail
      }
      ... on MessageEvent {
        ...MessageEventDetail
      }
      ... on MeetingEvent {
        ...MeetingEventDetail
      }
      ... on Quote {
        ...QuoteDetail
      }
      ... on PhoneCallEvent {
        ...PhoneCallEventDetail
      }
    }
  }
  ${TextMessageEventDetail}
  ${EmailMessageEventDetail}
  ${RequestEventDetail}
  ${MessageEventDetail}
  ${MeetingEventDetail}
  ${QuoteDetail}
  ${PhoneCallEventDetail}
`;

const orphanThreadIntoBrandMutation = gql`
  mutation OrphanThreadIntoBrand($orphanId: ID!, $brandId: ID!) {
    orphanThreadIntoBrand(orphanId: $orphanId, brandId: $brandId) {
      ...MatchDetail
    }
  }
  ${MatchDetail}
`;

const orphanFileUploadUpdatedSubscription = gql`
  subscription OrphanFileUploadUpdated($threadId: ID!) {
    fileUploadUpdated(threadId: $threadId) {
      ...FileUploadDetail
    }
  }
  ${FileUploadDetail}
`;

const orphanThreadSendTextMessageMutation = gql`
  mutation OrphanThreadSendTextMessage($input: ThreadSendTextMessageInput!) {
    threadSendTextMessage(input: $input) {
      ...TextMessageEventDetail
    }
  }
  ${TextMessageEventDetail}
`;

const orphanThreadSendEmailMessageMutation = gql`
  mutation OrphanThreadSendEmailMessage($input: ThreadSendEmailMessageInput!) {
    threadSendEmailMessage(input: $input) {
      ...EmailMessageEventDetail
    }
  }
  ${EmailMessageEventDetail}
`;

const orphanThreadOutboundPhoneCallMutation = gql`
  mutation OrphanThreadOutboundPhoneCall($recipientHuman: ID!, $orphanId: ID) {
    threadOutboundPhoneCall(
      recipientHuman: $recipientHuman
      orphanId: $orphanId
    ) {
      ...PhoneCallEventDetail
    }
  }
  ${PhoneCallEventDetail}
`;

const meetingInviteResponseOrphanMutation = gql`
  mutation MeetingInviteResponseOrphan($meetingId: ID!, $response: String!) {
    meetingInviteResponse(meetingId: $meetingId, response: $response) {
      ...MeetingEventDetail
    }
  }
  ${MeetingEventDetail}
`;

const orphanArchiveUnarchiveMutation = gql`
  mutation OrphanArchiveUnarchive($orphanId: ID!, $doArchive: Boolean!) {
    orphanArchiveUnarchive(orphanId: $orphanId, doArchive: $doArchive) {
      id
      isArchivedByExpert
    }
  }
`;

interface ExpertInboundDetailProps {
  emailTemplates: TemplatesQuery['templates'];
  expertDetails?: ExpertDetailsQuery['expertDetails'];
  expertId: string;
  onLoadData: (childData: OrphanDetailsQuery['orphanDetails']) => void;
  orphanId: string;
  socketClient: ClientWithOnReconnected;
}

interface IGroupedEvents {
  group: OrphanThreadPaginatedQuery['threadPaginated']['edges'];
}

let scrollDownQuickTimeout: NodeJS.Timeout | undefined;
let isLoadingEvents = false;
const PAGE_LIMIT = 20;

const ExpertInboundDetail = ({
  emailTemplates,
  expertDetails,
  expertId,
  onLoadData,
  orphanId,
  socketClient,
}: ExpertInboundDetailProps) => {
  const history = useHistory();
  const threadEventsWindow = useRef<HTMLDivElement>(null);
  const { addNotification } = useContext(GlobalNotificationContext);
  const [actionLoading, setActionLoading] = useState(false);
  const [threadSuccess, setThreadSuccess] = useState(false);
  const [showEncourageMerge, setShowEncourageMerge] = useState(true);
  const [initDate, setInitDate] = useState(() => new Date());
  const [sendLoading, setSendLoading] = useState(false);
  const [openActions, setOpenActions] = useState(false);
  const [currentAction, setCurrentAction] = useState('');
  const [emailDraft, setEmailDraft] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');
  const [emailFiles, setEmailFiles] = useState([] as IFilestackFileUpload[]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailRecipients, setEmailRecipients] = useState(
    [] as OrphanDetailsQuery['orphanDetails']['humans'],
  );
  const [textDraft, setTextDraft] = useState(false);
  const [textStatus, setTextStatus] = useState('');
  const [textFiles, setTextFiles] = useState([] as IFilestackFileUpload[]);
  const [textContent, setTextContent] = useState('');
  const [textRecipients, setTextRecipients] = useState(
    [] as OrphanDetailsQuery['orphanDetails']['humans'],
  );
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [tempEdges, setTempEdges] = useState(
    [] as OrphanThreadPaginatedQuery['threadPaginated']['edges'],
  );
  // remoteEventIds tracks threadEventUpdated things that might cause pagination errors
  const [remoteEventIds, setRemoteEventIds] = useState([] as string[]);
  const {
    data: dataOrphan,
    error: errorOrphan,
    loading: loadingOrphan,
    refetch: refetchOrphan,
  } = useQuery<OrphanDetailsQuery, OrphanDetailsQueryVariables>(
    orphanDetailsQuery,
    {
      onCompleted: (completedData) => {
        if (completedData && completedData.orphanDetails) {
          onLoadData(completedData.orphanDetails);
        }
      },
      returnPartialData: true,
      variables: {
        orphanId,
      },
    },
  );
  let fullErrorCover = '';
  if (errorOrphan) {
    fullErrorCover =
      errorUtils.getErrorMessage(errorOrphan) ||
      'Could not load thread details';
  }
  const orphanDetails =
    dataOrphan && dataOrphan.orphanDetails && dataOrphan.orphanDetails.id
      ? dataOrphan.orphanDetails
      : undefined;
  if (
    orphanDetails &&
    orphanDetails.migratedToMatchStr &&
    !actionLoading &&
    !threadSuccess
  ) {
    fullErrorCover =
      'This thread was merged into a client already: ' +
      orphanDetails.migratedToMatchStr;
  }
  if (
    orphanDetails &&
    orphanDetails.expertStr &&
    orphanDetails.expertStr !== expertId
  ) {
    fullErrorCover = 'This thread is no longer available';
  }
  useEffect(() => {
    setInitDate(new Date());
    setShowEncourageMerge(true);
  }, [orphanId]);
  const {
    data: dataThread,
    error: errorThread,
    loading: loadingThread,
    fetchMore: fetchMoreThread,
    subscribeToMore: subscribeToMoreThread,
  } = useQuery<OrphanThreadPaginatedQuery, OrphanThreadPaginatedQueryVariables>(
    orphanThreadPaginatedQuery,
    {
      onCompleted: () => {
        scrollDown();
      },
      variables: {
        direction: 'before',
        fromDate: initDate.getTime(),
        limit: PAGE_LIMIT,
        orphanId,
      },
    },
  );
  if (errorThread) {
    fullErrorCover =
      'Error loading this thread: ' + errorUtils.getErrorMessage(errorThread);
  }
  const cleanThread =
    (dataThread &&
      dataThread.threadPaginated &&
      dataThread.threadPaginated.edges) ||
    [];
  useEffect(() => {
    setTempEdges([]);
    setRemoteEventIds([]);
    setCurrentAction('');
    setOpenActions(false);
  }, [orphanId]);
  useEffect(() => {
    const unsub = subscribeToMoreThread<
      OrphanThreadEventUpdatedSubscription,
      OrphanThreadEventUpdatedSubscriptionVariables
    >({
      document: orphanThreadEventUpdatedSubscription,
      onError: (err) => {
        addNotification(
          'Error loading thread. Please refresh and try again: ' + err.message,
        );
        console.error('onError: subscribeToMoreThread', err);
      },
      updateQuery: (
        prev,
        {
          subscriptionData: {
            data: { threadEventUpdated },
          },
        },
      ) => {
        console.log('threadEventUpdated', prev, threadEventUpdated);
        if (!threadEventUpdated || !prev.threadPaginated) {
          return prev;
        }
        if (
          threadEventUpdated.__typename === 'RequestEvent' ||
          threadEventUpdated.__typename === 'Quote'
        ) {
          return prev;
        }
        if (
          (threadEventUpdated.__typename === 'MessageEvent' ||
            threadEventUpdated.__typename === 'EmailMessageEvent' ||
            threadEventUpdated.__typename === 'TextMessageEvent' ||
            threadEventUpdated.__typename === 'MeetingEvent' ||
            threadEventUpdated.__typename === 'PhoneCallEvent') &&
          prev.threadPaginated.id !== threadEventUpdated.orphanStr
        ) {
          return prev;
        }
        setTempEdges((prevTempEdges) =>
          prevTempEdges.filter(
            (t) =>
              t.id !== threadEventUpdated.threadEvent &&
              [
                'tempPhoneCallThreadEventId',
                'tempEmailMessageThreadEventId',
                'tempTextMessageThreadEventId',
                'tempMeetingThreadEventId',
              ].indexOf(t.id) === -1,
          ),
        );
        setRemoteEventIds((prevRemoteEventIds) =>
          prevRemoteEventIds.concat(threadEventUpdated.id),
        );
        const updatingIndex = (prev.threadPaginated.edges || []).findIndex(
          (e) => e.id === threadEventUpdated.threadEvent,
        );
        if (updatingIndex >= 0) {
          // update existing
          return {
            threadPaginated: {
              __typename: prev.threadPaginated.__typename,
              edges: prev.threadPaginated.edges
                .slice(0, updatingIndex)
                .concat({
                  __typename:
                    prev.threadPaginated.edges[updatingIndex].__typename,
                  cursor: prev.threadPaginated.edges[updatingIndex].cursor,
                  id: prev.threadPaginated.edges[updatingIndex].id,
                  node: {
                    ...prev.threadPaginated.edges[updatingIndex].node,
                    ...threadEventUpdated,
                  } as OrphanThreadPaginatedQuery['threadPaginated']['edges'][0]['node'],
                })
                .concat(prev.threadPaginated.edges.slice(updatingIndex + 1)),
              id: prev.threadPaginated.id,
            },
          };
        }
        // add new
        scrollDown();
        return {
          threadPaginated: {
            __typename: prev.threadPaginated.__typename,
            edges: (prev.threadPaginated.edges || []).concat({
              __typename: 'ThreadEventEdge',
              cursor: threadEventUpdated.createdAt,
              id: threadEventUpdated.threadEvent,
              node: threadEventUpdated,
            }),
            id: prev.threadPaginated.id,
          },
        };
      },
      variables: {
        threadId: orphanId,
      },
    });
    return () => {
      unsub();
    };
  }, [subscribeToMoreThread, orphanId, addNotification]);
  useEffect(() => {
    // check for drafts
    const hasEmailDraft = tokenUtils.readLocalStorage(
      `orphan-email-${orphanId}`,
    );
    setEmailDraft(!!hasEmailDraft);
    if (hasEmailDraft) {
      setEmailSubject(
        tokenUtils.readLocalStorage(`orphan-email-${orphanId}-subject`) || '',
      );
    } else {
      setEmailSubject('');
    }
    const hasTextDraft = tokenUtils.readLocalStorage(`orphan-text-${orphanId}`);
    setTextDraft(!!hasTextDraft);
    if (hasTextDraft) {
      setTextContent(
        tokenUtils.readLocalStorage(`orphan-text-${orphanId}-content`) || '',
      );
    } else {
      setTextContent('');
    }
  }, [orphanId]);
  const [tryArchiveUnarchive] = useMutation<
    OrphanArchiveUnarchiveMutation,
    OrphanArchiveUnarchiveMutationVariables
  >(orphanArchiveUnarchiveMutation);
  function doArchiveUnarchive(doArchive: boolean) {
    if (!orphanDetails) return;
    tryArchiveUnarchive({
      optimisticResponse: {
        orphanArchiveUnarchive: {
          __typename: 'Orphan',
          id: orphanId,
          isArchivedByExpert: doArchive,
        },
      },
      variables: {
        doArchive,
        orphanId,
      },
    })
      .then(() => {
        onLoadData({
          ...orphanDetails,
          isArchivedByExpert: doArchive,
        });
      })
      .catch((err: ApolloError) => {
        addNotification(
          errorUtils.getErrorMessage(err) || 'Archive/Unarchive Error',
        );
        logError(err, {
          component: 'ExpertInboundDetail',
          func: 'doArchiveUnarchive',
        });
      });
  }
  useSubscription<
    OrphanFileUploadUpdatedSubscription,
    OrphanFileUploadUpdatedSubscriptionVariables
  >(orphanFileUploadUpdatedSubscription, {
    variables: {
      threadId: orphanId,
    },
  });
  const [tryOutboundPhoneCall] = useMutation<
    OrphanThreadOutboundPhoneCallMutation,
    OrphanThreadOutboundPhoneCallMutationVariables
  >(orphanThreadOutboundPhoneCallMutation);
  function makePhoneCall() {
    if (sendLoading || !expertDetails) return;
    if (
      !orphanDetails ||
      !orphanDetails.humans ||
      !orphanDetails.humans[0] ||
      !orphanDetails.humans[0].primaryPhone
    ) {
      addNotification(
        'An error occured. Please refresh and try again.',
        undefined,
        5000,
      );
      return;
    }
    setSendLoading(true);
    tryOutboundPhoneCall({
      optimisticResponse: {
        threadOutboundPhoneCall: {
          __typename: 'PhoneCallEvent',
          createdAt: Date.now(),
          id: 'tempPhoneCallEventId',
          matchStr: null,
          orphanStr: orphanId,
          ownerExpert: {
            ...expertDetails,
            id: expertId,
          },
          ownerHuman: null,
          recipientExpert: null,
          recipientHumans: [orphanDetails.humans[0]],
          resultStatus: null,
          supportChannelStr: null,
          threadEvent: 'tempPhoneCallThreadEventId',
          voicemailRecordingUrl: null,
        },
      },
      update: (_cache, { data: dataPhoneCallMutation }) => {
        if (
          dataPhoneCallMutation &&
          dataPhoneCallMutation.threadOutboundPhoneCall
        ) {
          const nextTempEdges = tempEdges.filter(
            (e) =>
              e.id !== 'tempPhoneCallThreadEventId' &&
              e.id !==
                dataPhoneCallMutation.threadOutboundPhoneCall.threadEvent,
          );
          if (
            !cleanThread.find(
              (e) =>
                e.id ===
                dataPhoneCallMutation.threadOutboundPhoneCall.threadEvent,
            )
          ) {
            nextTempEdges.push({
              __typename: 'ThreadEventEdge',
              cursor: dataPhoneCallMutation.threadOutboundPhoneCall.createdAt,
              id: dataPhoneCallMutation.threadOutboundPhoneCall.threadEvent,
              node: dataPhoneCallMutation.threadOutboundPhoneCall,
            });
          }
          setTempEdges(_.uniqBy(nextTempEdges, 'id'));
        }
      },
      variables: {
        orphanId,
        recipientHuman: orphanDetails.humans[0].id,
      },
    })
      .then(() => {
        setCurrentAction('');
        setSendLoading(false);
      })
      .catch((err: ApolloError) => {
        setTempEdges(
          tempEdges.filter((e) => e.id !== 'tempPhoneCallThreadEventId'),
        );
        setSendLoading(false);
        addNotification(errorUtils.getErrorMessage(err) || 'Phone Call Error');
        logError(err, {
          component: 'ExpertInboundDetail',
          func: 'makePhoneCall',
        });
      });
  }
  const [trySendEmailMessage] = useMutation<
    OrphanThreadSendEmailMessageMutation,
    OrphanThreadSendEmailMessageMutationVariables
  >(orphanThreadSendEmailMessageMutation);
  function onSendEmail(drafHtml: string) {
    if (sendLoading || !expertDetails) return;
    if (!emailSubject.trim() || !emailRecipients.length || !drafHtml) {
      addNotification(
        'You need a subject, recipients, and an email body.',
        undefined,
        5000,
      );
      return;
    }
    setSendLoading(true);
    console.log('sendEmailMessage', emailSubject, drafHtml);
    trySendEmailMessage({
      optimisticResponse: {
        threadSendEmailMessage: {
          __typename: 'EmailMessageEvent',
          content: drafHtml,
          contentExpanded: null,
          createdAt: Date.now(),
          files: emailFiles.map((f, i) => ({
            ...f,
            __typename: 'FileUpload',
            createdAt: Date.now(),
            id: 'tempEmailMessageFileId' + i.toString(),
            isHiddenByExpert: false,
            isInlineEmail: false,
            matchStr: null,
            orphanStr: orphanId,
            supportChannelStr: null,
          })),
          id: 'tempEmailMessageEventId',
          isTransactional: false,
          matchStr: null,
          noExpertRecipientsFound: [],
          orphanStr: orphanId,
          ownerExpert: {
            ...expertDetails,
            id: expertId,
          },
          ownerHuman: null,
          quote: null,
          recipientCopiedExperts: [],
          recipientExpert: null,
          recipientHumans: emailRecipients,
          sendingError: null,
          sendingStatus: 'SENT',
          subject: emailSubject,
          supportChannelStr: null,
          threadEvent: 'tempEmailMessageThreadEventId',
        },
      },
      update: (_cache, { data: dataSentEmailMutation }) => {
        if (
          dataSentEmailMutation &&
          dataSentEmailMutation.threadSendEmailMessage
        ) {
          const nextTempEdges = tempEdges.filter(
            (e) =>
              e.id !== 'tempEmailMessageThreadEventId' &&
              e.id !== dataSentEmailMutation.threadSendEmailMessage.threadEvent,
          );
          if (
            !cleanThread.find(
              (e) =>
                e.id ===
                dataSentEmailMutation.threadSendEmailMessage.threadEvent,
            )
          ) {
            nextTempEdges.push({
              __typename: 'ThreadEventEdge',
              cursor: dataSentEmailMutation.threadSendEmailMessage.createdAt,
              id: dataSentEmailMutation.threadSendEmailMessage.threadEvent,
              node: dataSentEmailMutation.threadSendEmailMessage,
            });
          }
          setTempEdges(_.uniqBy(nextTempEdges, 'id'));
        }
      },
      variables: {
        input: {
          files: emailFiles,
          html: drafHtml,
          orphanId,
          recipientHumans: emailRecipients.map((r) => r.id),
          subject: emailSubject,
        },
      },
    })
      .then(() => {
        onDeleteEmailDraft();
        setSendLoading(false);
      })
      .catch((err: ApolloError) => {
        setTempEdges(
          tempEdges.filter((e) => e.id !== 'tempEmailMessageThreadEventId'),
        );
        setSendLoading(false);
        addNotification(errorUtils.getErrorMessage(err) || 'Send Email Error');
        logError(err, {
          component: 'ExpertInboundDetail',
          func: 'sendEmailMessage',
        });
      });
  }
  const [trySendTextMessage] = useMutation<
    OrphanThreadSendTextMessageMutation,
    OrphanThreadSendTextMessageMutationVariables
  >(orphanThreadSendTextMessageMutation);
  function onSendText() {
    if (sendLoading || !expertDetails) return;
    if (!textRecipients.length || !textContent) {
      addNotification(
        'You need recipients and a message body.',
        undefined,
        5000,
      );
      return;
    }
    setSendLoading(true);
    trySendTextMessage({
      optimisticResponse: {
        threadSendTextMessage: {
          __typename: 'TextMessageEvent',
          content: textContent,
          createdAt: Date.now(),
          files: textFiles.map((f, i) => ({
            ...f,
            __typename: 'FileUpload',
            createdAt: Date.now(),
            id: 'tempTextMessageFileId' + i.toString(),
            isHiddenByExpert: false,
            isInlineEmail: false,
            matchStr: null,
            orphanStr: orphanId,
            supportChannelStr: null,
          })),
          id: 'tempTextMessageEventId',
          isTransactional: false,
          matchStr: null,
          orphanStr: orphanId,
          ownerExpert: {
            ...expertDetails,
            id: expertId,
          },
          ownerHuman: null,
          quote: null,
          recipientExpert: null,
          recipientHumans: textRecipients,
          sendingError: null,
          supportChannelStr: null,
          threadEvent: 'tempTextMessageThreadEventId',
        },
      },
      update: (_cache, { data: dataSentTextMutation }) => {
        if (
          dataSentTextMutation &&
          dataSentTextMutation.threadSendTextMessage
        ) {
          const nextTempEdges = tempEdges.filter(
            (e) =>
              e.id !== 'tempTextMessageThreadEventId' &&
              e.id !== dataSentTextMutation.threadSendTextMessage.threadEvent,
          );
          if (
            !cleanThread.find(
              (e) =>
                e.id === dataSentTextMutation.threadSendTextMessage.threadEvent,
            )
          ) {
            nextTempEdges.push({
              __typename: 'ThreadEventEdge',
              cursor: dataSentTextMutation.threadSendTextMessage.createdAt,
              id: dataSentTextMutation.threadSendTextMessage.threadEvent,
              node: dataSentTextMutation.threadSendTextMessage,
            });
          }
          setTempEdges(_.uniqBy(nextTempEdges, 'id'));
        }
      },
      variables: {
        input: {
          content: textContent,
          files: textFiles,
          orphanId,
          recipientHuman: textRecipients[0].id,
        },
      },
    })
      .then(() => {
        onDeleteTextDraft();
        setSendLoading(false);
      })
      .catch((err: ApolloError) => {
        setTempEdges(
          tempEdges.filter((e) => e.id !== 'tempTextMessageThreadEventId'),
        );
        setSendLoading(false);
        addNotification(errorUtils.getErrorMessage(err) || 'Send Text Error');
        logError(err, {
          component: 'ExpertInboundDetail',
          func: 'sendTextMessage',
        });
      });
  }
  const [tryMeetingResponse] = useMutation<
    MeetingInviteResponseOrphanMutation,
    MeetingInviteResponseOrphanMutationVariables
  >(meetingInviteResponseOrphanMutation);
  function tryInviteResponse(
    meeting: Extract<
      OrphanThreadPaginatedQuery['threadPaginated']['edges'][0]['node'],
      { __typename?: 'MeetingEvent' | undefined }
    >['meeting'],
    response: string,
  ) {
    if (!expertDetails) {
      return;
    }
    tryMeetingResponse({
      optimisticResponse: {
        meetingInviteResponse: {
          __typename: 'MeetingEvent',
          action: null,
          createdAt: Date.now(),
          id: 'tempMeetingEventId',
          matchStr: null,
          meeting: {
            ...meeting,
            expertResponse: response,
          },
          orphanStr: orphanId,
          ownerExpert: {
            ...expertDetails,
            id: expertId,
          },
          ownerHuman: null,
          recipientExpert: null,
          recipientHumans: [
            {
              __typename: 'Human',
              firstName: '',
              id:
                (meeting.organizerHuman && meeting.organizerHuman.id) ||
                'tempMeetingEventHumanId',
              lastName: '',
              primaryEmail: '',
              primaryPhone: '',
            },
          ],
          response,
          supportChannelStr: null,
          threadEvent: 'tempMeetingThreadEventId',
        },
      },
      update: (_cache, { data: dataMeetingResponseMutation }) => {
        if (
          dataMeetingResponseMutation &&
          dataMeetingResponseMutation.meetingInviteResponse
        ) {
          const nextTempEdges = tempEdges.filter(
            (e) =>
              e.id !== 'tempMeetingThreadEventId' &&
              e.id !==
                dataMeetingResponseMutation.meetingInviteResponse.threadEvent,
          );
          if (
            !cleanThread.find(
              (e) =>
                e.id ===
                dataMeetingResponseMutation.meetingInviteResponse.threadEvent,
            )
          ) {
            nextTempEdges.push({
              __typename: 'ThreadEventEdge',
              cursor:
                dataMeetingResponseMutation.meetingInviteResponse.createdAt,
              id: dataMeetingResponseMutation.meetingInviteResponse.threadEvent,
              node: dataMeetingResponseMutation.meetingInviteResponse,
            });
          }
          setTempEdges(_.uniqBy(nextTempEdges, 'id'));
        }
      },
      variables: {
        meetingId: meeting.id,
        response,
      },
    }).catch((err: ApolloError) => {
      setTempEdges(
        tempEdges.filter((e) => e.id !== 'tempMeetingThreadEventId'),
      );
      addNotification(
        errorUtils.getErrorMessage(err) || 'Meeting Response Error',
      );
      logError(err, {
        component: 'ExpertInboundDetail',
        func: 'tryMeetingResponse',
      });
    });
  }
  function onReplyClick(
    edge: OrphanThreadPaginatedQuery['threadPaginated']['edges'][0],
  ) {
    console.log('onReplyClick', edge.id);
    if (edge.node.__typename === 'EmailMessageEvent') {
      const newSubject = replyToSubject(edge.node.subject);
      openEmailEditor(newSubject);
    } else if (edge.node.__typename === 'MeetingEvent') {
      const newSubject = replyToSubject(
        edge.node.meeting && edge.node.meeting.eventTitle,
      );
      openEmailEditor(newSubject);
    } else if (edge.node.__typename === 'TextMessageEvent') {
      openTextEditor();
    } else if (edge.node.__typename === 'PhoneCallEvent') {
      openTextEditor();
    } else if (edge.node.__typename === 'MessageEvent') {
      openEmailEditor();
    } else {
      scrollDown();
    }
  }
  if (loadingOrphan && loadingThread) {
    // ignore these
  }
  function groupThreadEvents(
    events: OrphanThreadPaginatedQuery['threadPaginated']['edges'],
  ) {
    const groupedEvents: IGroupedEvents[] = [];
    events.forEach((ev) => {
      let isSameEventType = false;
      let isSameSenderSide = false;
      let isSameHuman = false;
      let isCurrGroupable = false;
      let isLastGroupable = false;
      const isFirstGroup = !groupedEvents.length;
      if (
        !isFirstGroup &&
        (ev.node.__typename === 'TextMessageEvent' ||
          ev.node.__typename === 'MessageEvent' ||
          ev.node.__typename === 'PhoneCallEvent')
      ) {
        isCurrGroupable = true;
        isLastGroupable = true;
        const lastGroup = groupedEvents[groupedEvents.length - 1];
        const lastGroupEvent = lastGroup.group[0];
        // is same event type as last
        if (lastGroupEvent.node.__typename === ev.node.__typename) {
          isSameEventType = true;
          // is both sent from expert or both from Human
          isSameSenderSide =
            !!(ev.node.ownerHuman && lastGroupEvent.node.ownerHuman) ||
            !!(ev.node.ownerExpert && lastGroupEvent.node.ownerExpert);
          // is human interacting same
          isSameHuman =
            (ev.node.ownerHuman &&
              lastGroupEvent.node.ownerHuman &&
              ev.node.ownerHuman.id === lastGroupEvent.node.ownerHuman.id) ||
            (ev.node.recipientHumans[0] &&
              lastGroupEvent.node.recipientHumans[0] &&
              ev.node.recipientHumans[0].id ===
                lastGroupEvent.node.recipientHumans[0].id);
          // don't group texts with quotes attached
          if (ev.node.__typename === 'TextMessageEvent') {
            if (ev.node.quote) {
              isCurrGroupable = false;
            }
          }
          if (lastGroupEvent.node.__typename === 'TextMessageEvent') {
            if (lastGroupEvent.node.quote) {
              isLastGroupable = false;
            }
          }
        }
      }
      if (
        isFirstGroup ||
        !isCurrGroupable ||
        !isLastGroupable ||
        !isSameEventType ||
        !isSameSenderSide ||
        !isSameHuman
      ) {
        groupedEvents.push({
          group: [ev],
        });
      } else {
        groupedEvents[groupedEvents.length - 1].group.push(ev);
      }
    });
    return groupedEvents;
  }
  const threadEventEdges = _.uniqBy(cleanThread.concat(tempEdges), 'id').sort(
    (a, b) => a.cursor - b.cursor,
  );
  console.log('threadEventEdges', threadEventEdges);
  const countYourMessages = threadEventEdges.filter((e) => {
    if (
      e.node &&
      !e.node.ownerHuman &&
      (e.node.__typename === 'MessageEvent' ||
        e.node.__typename === 'EmailMessageEvent' ||
        e.node.__typename === 'TextMessageEvent' ||
        e.node.__typename === 'MeetingEvent' ||
        e.node.__typename === 'PhoneCallEvent' ||
        e.node.__typename === 'Quote')
    ) {
      return e.node.ownerExpert && e.node.ownerExpert.id === expertId;
    }
    return false;
  }).length;
  const overPageLimit = threadEventEdges.length >= PAGE_LIMIT;
  function scrollDown() {
    scrollDownQuickTimeout = setTimeout(() => {
      if (threadEventsWindow && threadEventsWindow.current) {
        threadEventsWindow.current.scrollTop =
          threadEventsWindow.current.scrollHeight;
      }
    }, 5);
  }
  useEffect(() => {
    function onScroll() {
      if (threadEventsWindow && threadEventsWindow.current) {
        const scrollTemp = threadEventsWindow.current.scrollTop;
        if (
          scrollTemp < 500 &&
          overPageLimit &&
          canLoadMore &&
          !isLoadingEvents
        ) {
          isLoadingEvents = true;
          const heightTemp = threadEventsWindow.current.scrollHeight;
          fetchMoreThread({
            updateQuery: (prev, { fetchMoreResult }) => {
              if (
                !fetchMoreResult ||
                !prev.threadPaginated ||
                !fetchMoreResult.threadPaginated
              )
                return prev;
              if (fetchMoreResult.threadPaginated.edges.length < PAGE_LIMIT) {
                setCanLoadMore(false);
              }
              return {
                threadPaginated: {
                  __typename: prev.threadPaginated.__typename,
                  edges: _.uniqBy(
                    fetchMoreResult.threadPaginated.edges.concat(
                      prev.threadPaginated.edges || [],
                    ),
                    'id',
                  ),
                  id: prev.threadPaginated.id,
                },
              };
            },
            variables: {
              direction: 'before',
              fromDate: threadEventEdges.filter(
                (ev) => ev.node.id && remoteEventIds.indexOf(ev.node.id) === -1,
              )[0].cursor,
              limit: PAGE_LIMIT,
              orphanId,
            },
          })
            .catch((err: ApolloError) => {
              addNotification(
                errorUtils.getErrorMessage(err) || 'Load More Error',
              );
              logError(err, {
                component: 'ExpertInboundDetail',
                func: 'fetchMoreThread',
              });
            })
            .finally(() => {
              isLoadingEvents = false;
              if (threadEventsWindow && threadEventsWindow.current) {
                threadEventsWindow.current.scrollTop =
                  Math.max(
                    threadEventsWindow.current.scrollHeight - heightTemp,
                    0,
                  ) + scrollTemp;
              }
            });
        }
      }
    }
    if (threadEventsWindow && threadEventsWindow.current) {
      threadEventsWindow.current.addEventListener('scroll', onScroll);
    }
    const tempWindow = threadEventsWindow.current;
    return () => {
      if (tempWindow) {
        tempWindow.removeEventListener('scroll', onScroll);
      }
    };
  }, [
    canLoadMore,
    overPageLimit,
    addNotification,
    fetchMoreThread,
    orphanId,
    threadEventEdges,
    remoteEventIds,
  ]);
  useEffect(() => {
    scrollDown();
    const scrollDownEffectTimeout = setTimeout(() => {
      scrollDown();
    }, 500);
    return () => {
      if (scrollDownEffectTimeout) {
        clearTimeout(scrollDownEffectTimeout);
      }
      if (scrollDownQuickTimeout) {
        clearTimeout(scrollDownQuickTimeout);
      }
    };
  }, [orphanId]);
  useEffect(() => {
    const loadTime = new Date();
    const reconnectedListener = socketClient.onReconnected(() => {
      console.log('ExpertInboundDetail socketClient onReconnected');
      refetchOrphan().catch(() => {});
      fetchMoreThread({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (
            !fetchMoreResult ||
            !prev.threadPaginated ||
            !fetchMoreResult.threadPaginated
          )
            return prev;
          return {
            threadPaginated: {
              __typename: prev.threadPaginated.__typename,
              edges: _.uniqBy(
                fetchMoreResult.threadPaginated.edges.concat(
                  prev.threadPaginated.edges || [],
                ),
                'id',
              ),
              id: prev.threadPaginated.id,
            },
          };
        },
        variables: {
          direction: 'after',
          fromDate: loadTime.getTime(),
          limit: 50,
          orphanId,
        },
      }).catch((err: ApolloError) => {
        addNotification(errorUtils.getErrorMessage(err) || 'Reconnect Error');
        logError(err, {
          component: 'ExpertInboundDetail',
          func: 'reconnect fetchMoreThread',
        });
      });
    });
    return () => {
      reconnectedListener();
    };
  }, [socketClient, refetchOrphan, fetchMoreThread, addNotification, orphanId]);
  const threadEventGroups = groupThreadEvents(threadEventEdges);
  function generateComponentsForGroup(
    groupedEvents: OrphanThreadPaginatedQuery['threadPaginated']['edges'],
  ) {
    return groupedEvents
      .map((ev, i) => {
        if (
          ev.node.__typename === 'RequestEvent' ||
          ev.node.__typename === 'Quote'
        ) {
          return <div key={ev.id} />;
        }
        if (ev.node.__typename === 'MessageEvent') {
          return (
            <MessageThreadEvent
              key={ev.id}
              expertId={expertId}
              isGroupFirst={!i}
              isGroupLast={i === groupedEvents.length - 1}
              messageEvent={ev.node}
              threadEventId={ev.id}
              threadEventTimestamp={ev.cursor}
              onReplyClick={() => onReplyClick(ev)}
            />
          );
        }
        if (ev.node.__typename === 'EmailMessageEvent') {
          return (
            <EmailMessageThreadEvent
              key={ev.id}
              expertId={expertId}
              emailMessageEvent={ev.node}
              threadEventId={ev.id}
              threadEventTimestamp={ev.cursor}
              onReplyClick={() => onReplyClick(ev)}
            />
          );
        }
        if (ev.node.__typename === 'TextMessageEvent') {
          return (
            <TextMessageThreadEvent
              key={ev.id}
              expertId={expertId}
              isGroupFirst={!i}
              isGroupLast={i === groupedEvents.length - 1}
              textMessageEvent={ev.node}
              threadEventId={ev.id}
              threadEventTimestamp={ev.cursor}
              onReplyClick={() => onReplyClick(ev)}
            />
          );
        }
        if (ev.node.__typename === 'PhoneCallEvent') {
          return (
            <PhoneCallThreadEvent
              key={ev.id}
              isGroupFirst={!i}
              isGroupLast={i === groupedEvents.length - 1}
              phoneCallEvent={ev.node}
              threadEventId={ev.id}
              threadEventTimestamp={ev.cursor}
              onReplyClick={() => onReplyClick(ev)}
            />
          );
        }
        if (ev.node.__typename === 'MeetingEvent') {
          return (
            <MeetingThreadEvent
              key={ev.id}
              expertDetails={expertDetails}
              meetingEvent={ev.node}
              threadEventId={ev.id}
              threadEventTimestamp={ev.cursor}
              onReplyClick={() => onReplyClick(ev)}
              onInviteResponse={tryInviteResponse}
            />
          );
        }
        console.log('Missing ThreadEvent type');
        return null;
      })
      .filter((c) => c);
  }
  function closeCurrentAction() {
    setCurrentAction('');
    scrollDown();
  }
  function openPhoneCallEditor() {
    // check currentAction
    if (currentAction) {
      scrollDown();
      return;
    }
    setCurrentAction('PHONE');
    setOpenActions(false);
    scrollDown();
  }
  function openOrphanClientEditor() {
    // check currentAction
    if (currentAction) {
      scrollDown();
      return;
    }
    setCurrentAction('ORPHAN-CLIENT');
    setOpenActions(false);
    scrollDown();
  }
  function openEmailEditor(subject?: string, content?: string) {
    // check currentAction
    if (currentAction || !orphanDetails) {
      scrollDown();
      return;
    }
    // check draft
    if (emailDraft) {
      subject =
        tokenUtils.readLocalStorage(`orphan-email-${orphanId}-subject`) || '';
      content =
        tokenUtils.readLocalStorage(`orphan-email-${orphanId}-content`) || '';
    }
    if (subject === undefined && content === undefined) {
      const lastEmail = _.findLast(
        threadEventEdges,
        (ev) => ev.node.__typename === 'EmailMessageEvent',
      );
      // look for the latest email
      if (lastEmail) {
        // simulate as if they clicked that email
        onReplyClick(lastEmail);
        return;
      }
    }
    setCurrentAction('EMAIL');
    setOpenActions(false);
    scrollDown();
    setEmailSubject(subject || '');
    setEmailContent(content || '');
    setEmailRecipients(orphanDetails.humans);
    setEmailFiles([]);
    setEmailStatus(emailDraft ? 'Saved' : '');
  }
  const debouncedEmailDraft = useDebouncedCallback(() => {
    tokenUtils.writeLocalStorage(`orphan-email-${orphanId}`, 'true');
    tokenUtils.writeLocalStorage(
      `orphan-email-${orphanId}-subject`,
      emailSubject,
    );
    tokenUtils.writeLocalStorage(
      `orphan-email-${orphanId}-content`,
      emailContent,
    );
    setEmailDraft(true);
    setEmailStatus('Saved');
  }, 1000);
  function onDeleteEmailDraft() {
    debouncedEmailDraft.cancel();
    tokenUtils.removeLocalStorage(`orphan-email-${orphanId}`);
    tokenUtils.removeLocalStorage(`orphan-email-${orphanId}-subject`);
    tokenUtils.removeLocalStorage(`orphan-email-${orphanId}-content`);
    setEmailDraft(false);
    setEmailStatus('');
    setEmailSubject('');
    setEmailContent('');
    setEmailRecipients([]);
    setEmailFiles([]);
    setCurrentAction('');
  }
  function onSaveEmailDraft(
    subject: string,
    draftContent: string | null,
    files: IFilestackFileUpload[],
  ) {
    let saveDraft = false;
    if (emailSubject !== subject) {
      saveDraft = true;
      setEmailSubject(subject || '');
    }
    if (emailFiles !== files) {
      setEmailFiles(files || []);
    }
    if (draftContent !== null && emailContent !== draftContent) {
      saveDraft = true;
      setEmailContent(draftContent);
    }
    if (saveDraft) {
      setEmailStatus('Saving');
      debouncedEmailDraft.callback();
    }
  }
  function openTextEditor(content?: string) {
    // check currentAction
    if (currentAction || !orphanDetails) {
      scrollDown();
      return;
    }
    // check draft
    if (textDraft) {
      content =
        tokenUtils.readLocalStorage(`orphan-text-${orphanId}-content`) || '';
    }
    setCurrentAction('TEXT');
    setOpenActions(false);
    scrollDown();
    setTextContent(content || '');
    setTextRecipients(orphanDetails.humans);
    setTextFiles([]);
    setTextStatus(textDraft ? 'Saved' : '');
  }
  const debouncedTextDraft = useDebouncedCallback(() => {
    tokenUtils.writeLocalStorage(`orphan-text-${orphanId}`, 'true');
    tokenUtils.writeLocalStorage(
      `orphan-text-${orphanId}-content`,
      textContent,
    );
    setTextDraft(true);
    setTextStatus('Saved');
  }, 1000);
  function onDeleteTextDraft() {
    debouncedTextDraft.cancel();
    tokenUtils.removeLocalStorage(`orphan-text-${orphanId}`);
    tokenUtils.removeLocalStorage(`orphan-text-${orphanId}-content`);
    setTextDraft(false);
    setTextStatus('');
    setTextContent('');
    setTextRecipients([]);
    setTextFiles([]);
    setCurrentAction('');
  }
  function onSaveTextDraft(content: string, files: IFilestackFileUpload[]) {
    let saveDraft = false;
    if (textFiles !== files) {
      setTextFiles(files || []);
    }
    if (textContent !== content) {
      saveDraft = true;
      setTextContent(content);
    }
    if (saveDraft) {
      setTextStatus('Saving');
      debouncedTextDraft.callback();
    }
  }
  const [tryThreadBrand] = useMutation<
    OrphanThreadIntoBrandMutation,
    OrphanThreadIntoBrandMutationVariables
  >(orphanThreadIntoBrandMutation);
  function onSelectBrand(brandId: string) {
    if (actionLoading || threadSuccess || !dataThread) return;
    setActionLoading(true);
    tryThreadBrand({
      update: (cache) => {
        cache.writeFragment({
          data: {
            ...dataThread.threadPaginated,
            edges: [],
          },
          fragment: gql`
            fragment ThreadBrand on Thread {
              id
              edges {
                id
                cursor
                node {
                  ... on TextMessageEvent {
                    ...TextMessageEventDetail
                  }
                  ... on EmailMessageEvent {
                    ...EmailMessageEventDetail
                  }
                  ... on RequestEvent {
                    ...RequestEventDetail
                  }
                  ... on MessageEvent {
                    ...MessageEventDetail
                  }
                  ... on MeetingEvent {
                    ...MeetingEventDetail
                  }
                  ... on Quote {
                    ...QuoteDetail
                  }
                  ... on PhoneCallEvent {
                    ...PhoneCallEventDetail
                  }
                }
              }
            }
            ${TextMessageEventDetail}
            ${EmailMessageEventDetail}
            ${RequestEventDetail}
            ${MessageEventDetail}
            ${MeetingEventDetail}
            ${QuoteDetail}
            ${PhoneCallEventDetail}
          `,
          fragmentName: 'ThreadBrand',
          id: '',
        });
      },
      variables: {
        brandId,
        orphanId,
      },
    })
      .then(({ data: mutationData }) => {
        setThreadSuccess(true);
        setActionLoading(false);
        if (mutationData && mutationData.orphanThreadIntoBrand) {
          history.push('/clients/' + mutationData.orphanThreadIntoBrand.id);
        } else {
          setThreadSuccess(false);
        }
      })
      .catch((err: ApolloError) => {
        setActionLoading(false);
        addNotification(
          errorUtils.getErrorMessage(err) || 'Merge Thread Error',
        );
        logError(err, {
          component: 'OrphanClientEditor',
          func: 'tryThreadBrand',
        });
      });
  }
  // everyone has an email set
  const showEmailActions = !!(
    orphanDetails &&
    orphanDetails.humans &&
    !orphanDetails.humans.find((h) => !h.primaryEmail)
  );
  // only one person on thread and they have a phone set
  const showPhoneActions = !!(
    orphanDetails &&
    orphanDetails.humans &&
    orphanDetails.humans.length === 1 &&
    orphanDetails.humans[0].primaryPhone
  );
  function hideEncourageMerge() {
    setShowEncourageMerge(false);
    openOrphanClientEditor();
  }
  function renderCurrentActionEditor() {
    if (currentAction === 'EMAIL') {
      return (
        <div
          className={
            'ThreadActionEmail ' +
            (sendLoading ? ' ThreadActionEmailSending ' : '')
          }
        >
          <EmailEditor
            emailTemplates={emailTemplates}
            expertId={expertId}
            expertEmailAddress={
              expertDetails && expertDetails.primaryPublicEmail
                ? expertDetails.primaryPublicEmail
                : ''
            }
            expertPhoneNumber={
              expertDetails && expertDetails.primaryPublicPhone
                ? expertDetails.primaryPublicPhone
                : ''
            }
            expertSchedulePath={
              expertDetails &&
              expertDetails.scheduleTimeEnabled &&
              expertDetails.profilePath
                ? expertDetails.profilePath
                : ''
            }
            files={emailFiles}
            fixedRecipients={emailRecipients}
            initContent={emailContent}
            onDeleteDraft={onDeleteEmailDraft}
            onSaveDraft={onSaveEmailDraft}
            onSendEmail={onSendEmail}
            recipients={[]}
            subject={emailSubject}
            status={emailStatus}
          />
        </div>
      );
    }
    if (currentAction === 'TEXT') {
      return (
        <div
          className={
            'ThreadActionText ' +
            (sendLoading ? ' ThreadActionTextSending ' : '')
          }
        >
          <TextEditor
            content={textContent}
            expertId={expertId}
            expertEmailAddress={
              expertDetails && expertDetails.primaryPublicEmail
                ? expertDetails.primaryPublicEmail
                : ''
            }
            expertPhoneNumber={
              expertDetails && expertDetails.primaryPublicPhone
                ? expertDetails.primaryPublicPhone
                : ''
            }
            expertSchedulePath={
              expertDetails &&
              expertDetails.scheduleTimeEnabled &&
              expertDetails.profilePath
                ? expertDetails.profilePath
                : ''
            }
            files={textFiles}
            fixedRecipients={textRecipients}
            onDeleteDraft={onDeleteTextDraft}
            onSaveDraft={onSaveTextDraft}
            onSendText={onSendText}
            recipients={[]}
            status={textStatus}
          />
        </div>
      );
    }
    if (currentAction === 'PHONE' && orphanDetails && orphanDetails.humans) {
      return (
        <div
          className={
            'ThreadActionPhone ' +
            (sendLoading ? ' ThreadActionPhoneSending ' : '')
          }
        >
          <PhoneCallEditor
            fixedRecipients={orphanDetails.humans}
            onCancel={() => setCurrentAction('')}
            onMakeCall={makePhoneCall}
            team={[]}
          />
        </div>
      );
    }
    if (
      currentAction === 'ORPHAN-CLIENT' &&
      orphanDetails &&
      orphanDetails.humans
    ) {
      return (
        <div className="ThreadActionOrphanClient">
          <OrphanClientEditor
            humans={orphanDetails.humans}
            onCancel={() => setCurrentAction('')}
            onSelectBrand={onSelectBrand}
            isActionLoading={actionLoading}
          />
        </div>
      );
    }
    return null;
  }
  return (
    <div className="ExpertInboundDetail">
      <div className="ThreadDetailMainBody" ref={threadEventsWindow}>
        <div
          className={
            'ThreadEvents ' + (currentAction ? ' ThreadEventsHasAction ' : '')
          }
        >
          {threadEventGroups.map((evGroup) => (
            <div className="ThreadEventGroup" key={evGroup.group[0].id}>
              {generateComponentsForGroup(evGroup.group)}
            </div>
          ))}
          {!!showEncourageMerge && countYourMessages >= 2 && (
            <div className="InboundEncourageMerge" onClick={hideEncourageMerge}>
              <div className="InboundEncourageMergeTitle">Create a client?</div>
              <div className="InboundEncourageMergeBody">
                You&apos;ve already sent a few messages and should merge this
                thread into an existing client or create a new one. This keeps
                your dashboard organized and enables our Support team to help
                you manage clients &amp; conversations.
              </div>
            </div>
          )}
        </div>
        {!!currentAction && !!orphanDetails && (
          <div className="ThreadCurrentAction">
            {renderCurrentActionEditor()}
          </div>
        )}
      </div>
      <div
        className={
          'ThreadDetailActions ' +
          (openActions ? ' ThreadDetailActionsOpen ' : '') +
          (currentAction ? ' ThreadDetailActionsCurrent ' : '')
        }
      >
        <div className="ThreadDetailActionRow">
          {!!showPhoneActions && (
            <Fragment>
              <div
                className="ThreadDetailActionItem"
                onClick={() => openPhoneCallEditor()}
              >
                <span>make a </span>call
              </div>
              <div
                className="ThreadDetailActionItem"
                onClick={() => openTextEditor()}
              >
                <span>send a </span>text
              </div>
            </Fragment>
          )}
          {!!showEmailActions && (
            <div
              className="ThreadDetailActionItem"
              onClick={() => openEmailEditor()}
            >
              <span>send an </span>email
            </div>
          )}
        </div>
        <div className="ThreadDetailActionRow">
          <div
            className="ThreadDetailActionTrigger"
            onClick={() =>
              currentAction
                ? closeCurrentAction()
                : setOpenActions(!openActions)
            }
          >
            <div className="ThreadDetailActionBtn">+</div>
            <div className="ThreadDetailActionText">actions</div>
          </div>
          <div
            className="ThreadDetailActionItem"
            onClick={() => openOrphanClientEditor()}
          >
            move to clients
          </div>
          {!!orphanDetails && (
            <div
              className="ThreadDetailActionItem"
              onClick={() =>
                doArchiveUnarchive(!orphanDetails.isArchivedByExpert)
              }
            >
              {orphanDetails.isArchivedByExpert ? 'unarchive' : 'archive'}
              <span> thread</span>
            </div>
          )}
        </div>
      </div>
      {!!fullErrorCover && (
        <div className="DashboardErrorCover">
          <div className="DashboardErrorCoverOver" />
          <div className="DashboardErrorCoverPop">
            <Link to="/inbound" className="DashboardErrorCoverNav">
              back
            </Link>
            <div className="DashboardErrorCoverContent">{fullErrorCover}</div>
          </div>
        </div>
      )}
    </div>
  );
};

ExpertInboundDetail.propTypes = {
  emailTemplates: PropTypes.array.isRequired,
  expertDetails: PropTypes.object,
  expertId: PropTypes.string.isRequired,
  onLoadData: PropTypes.func.isRequired,
  orphanId: PropTypes.string.isRequired,
  socketClient: PropTypes.object.isRequired,
};

ExpertInboundDetail.defaultProps = {
  expertDetails: null,
};

export default ExpertInboundDetail;
